import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type AnimationContextProps = {
  areAnimationsEnabled: boolean;
  setAreAnimationsEnabled: Dispatch<SetStateAction<boolean>>;
};

const defaultAnimationContext: AnimationContextProps = {
  areAnimationsEnabled: true,
  setAreAnimationsEnabled: () => {},
};

export const AnimationContext = createContext<AnimationContextProps>(defaultAnimationContext);

type AnimationProviderProps = {
  children: ReactNode;
};

const LOCAL_STORAGE_KEY = 'areAnimationsEnabled';

export function AnimationProvider({ children }: Readonly<AnimationProviderProps>) {
  const [areAnimationsEnabled, setAreAnimationsEnabled] = useState<boolean>(() => {
    const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) : true;
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(areAnimationsEnabled));
  }, [areAnimationsEnabled]);

  const contextValue = useMemo(
    () => ({ areAnimationsEnabled, setAreAnimationsEnabled }),
    [areAnimationsEnabled, setAreAnimationsEnabled],
  );

  return <AnimationContext.Provider value={contextValue}>{children}</AnimationContext.Provider>;
}

export const useAnimationsEnabled = () => {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimationsEnabled must be used within an AnimationProvider');
  }
  return context;
};
